import { Link } from "react-router-dom";
import { Heart, Phone, Mail, MapPin, Facebook, Instagram, MessageCircle } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import NewsletterForm from "@/components/NewsletterForm";

const QUICK_LINKS = [
  { to: "/la-maladie", label: "La maladie de Waldmann", id: "footer-link-maladie" },
  { to: "/vivre-avec", label: "Vivre avec la maladie", id: "footer-link-vivre" },
  { to: "/suivi-medical", label: "Suivi médical", id: "footer-link-suivi" },
  { to: "/nos-actions", label: "Nos actions", id: "footer-link-actions" },
  { to: "/livre-espoir", label: "Livre d'espoir", id: "footer-link-livre-espoir" },
  { to: "/actualites", label: "Actualités & ressources", id: "footer-link-actualites" },
  { to: "/contact", label: "Contact", id: "footer-link-contact" },
];

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/share/1EF21VWRPr/", id: "social-facebook" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/associationwaldmann?rpxt=cmFveGxqY2l3M3Bu", id: "social-instagram" },
  { icon: MessageCircle, label: "WhatsApp", href: "https://wa.me/33954243446", id: "social-whatsapp" },
];

const Footer = () => (
  <>
    <footer data-testid="site-footer" className="bg-[#0F2C59] text-white">
      <div className="container-site pt-16 pb-28 md:pb-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="inline-flex items-center gap-2.5">
              <LogoMark size={40} />
              <span className="font-heading text-lg font-extrabold tracking-tight">
                Aide au Monde <span className="text-[#FF6B6B]">&amp;</span> Santé
              </span>
            </span>
            <p className="mt-5 text-sm leading-relaxed text-sky-100/80">
              Association dédiée à la maladie de Waldmann. Nous soutenons les malades et leurs
              proches, faisons connaître cette maladie rare et finançons l'amélioration de la
              prise en charge.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={s.id}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-sky-100 transition-[background-color,color,transform] duration-200 hover:bg-[#FF6B6B] hover:border-[#FF6B6B] hover:text-white hover:-translate-y-0.5"
                >
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label="Liens rapides">
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-sky-300">
              Navigation
            </h3>
            <ul className="mt-5 space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="text-sm font-medium text-sky-100/80 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-sky-300">
              Contact
            </h3>
            <ul className="mt-5 space-y-4 text-sm text-sky-100/80">
              <li className="flex gap-3">
                <MapPin size={17} className="mt-0.5 shrink-0 text-[#FF6B6B]" />
                <span data-testid="footer-address">
                  Aide au Monde et Santé
                  <br />
                  2 rue des Amandiers
                  <br />
                  95200 Sarcelles
                </span>
              </li>
              <li>
                <a
                  href="tel:+33954243446"
                  data-testid="footer-phone-link"
                  className="flex items-center gap-3 font-semibold text-white transition-colors hover:text-[#FF6B6B]"
                >
                  <Phone size={17} className="shrink-0 text-[#FF6B6B]" />
                  09 54 24 34 46
                </a>
              </li>
              <li>
                <a
                  href="mailto:aide.monde.et.sante@gmail.com"
                  data-testid="footer-email-link"
                  className="flex items-center gap-3 transition-colors hover:text-white"
                >
                  <Mail size={17} className="shrink-0 text-[#FF6B6B]" />
                  aide.monde.et.sante@gmail.com
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-sky-300">
              Newsletter
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-sky-100/80">
              Recevez nos actualités et conseils, environ une fois par mois.
            </p>
            <div className="mt-4">
              <NewsletterForm dark />
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-sky-100/60 sm:flex-row">
          <p>© 2026 Aide au Monde et Santé — Association loi 1901, n° RNA W952012935. Tous droits réservés.</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="transition-colors hover:text-white">
              Mentions légales
            </Link>
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="transition-colors hover:text-white">
              Politique de confidentialité (RGPD)
            </Link>
            <Link to="/contact" data-testid="footer-accessibility-link" className="transition-colors hover:text-white">
              Accessibilité
            </Link>
          </div>
        </div>
      </div>
    </footer>

    {/* Barre de don fixe sur mobile */}
    <div
      data-testid="sticky-mobile-donate-bar"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur-xl md:hidden"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <Link
        to="/faire-un-don"
        data-testid="sticky-donate-button"
        className="btn-don w-full !py-3.5"
      >
        <Heart size={18} strokeWidth={2.5} fill="currentColor" />
        Faire un don
      </Link>
    </div>
  </>
);

export default Footer;
